import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useLlmService } from "../../../../services";
import { LLM_MODEL_TYPES, LLMSettings, SettingType } from "../../../../pages/Settings/types";
import { setLLMSettings } from "../../../../redux/slices/settings";
import React, { useState, useRef, useEffect, KeyboardEvent, useMemo } from "react";
import { HiChevronDown } from "react-icons/hi2";
import { useJwt } from "../../../../helpers/common";

// Model configuration with display names and descriptions
const MODEL_CONFIG = {
  [LLM_MODEL_TYPES.GEMINI_3_1_PRO]: {
    name: "Gemini 3.1 Pro",
    description: "Latest and most powerful model"
  },
  [LLM_MODEL_TYPES.GEMINI_3_0_PRO]: {
    name: "Gemini 3 Pro",
    description: "Powerful model for complex tasks"
  },
  [LLM_MODEL_TYPES.GEMINI_3_0_FLASH]: {
    name: "Gemini 3 Flash",
    description: "Fast and efficient model"
  }
} as const;

const ChangeLLMModel = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateLlmModel } = useLlmService();
  const jwtData: any = useJwt();
  const isAdmin = typeof jwtData?.org_role === 'string' && String(jwtData?.org_role).toLowerCase() === 'admin';

  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [menuMounted, setMenuMounted] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { llmSettings }: { llmSettings: LLMSettings } = useSelector((state: any) => state.settings);

  const currentModel = llmSettings?.model || LLM_MODEL_TYPES.GEMINI_3_0_FLASH;
  const llmModelLabel = useMemo(() => 
    MODEL_CONFIG[currentModel as keyof typeof MODEL_CONFIG]?.name || currentModel,
    [currentModel]
  );
  const canChangeLlmModel = !!llmSettings?.api_key;

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        isModelMenuOpen
      ) {
        setIsModelMenuOpen(false);
      }
    };

    // Handle escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModelMenuOpen) {
        setIsModelMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown as unknown as EventListener);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown as unknown as EventListener);
    };
  }, [isModelMenuOpen]);

  const onChangeLLMModel = (model: LLM_MODEL_TYPES) => {
    if (model !== currentModel) {
      dispatch(setLLMSettings({
        model: model,
        api_key: llmSettings.api_key,
        provider: llmSettings.provider
      }));
      updateLlmModel(model);
    }
  };

  return (
    <div className="relative h-8" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => {
          setIsModelMenuOpen(v => !v);
          setMenuMounted(true);
        }}
        className="h-full px-2.5 rounded-xl text-[12px] sm:text-[13px] dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700
        dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1 shadow-sm focus-visible:shadow-none focus-visible:ring-0 focus-visible:outline-none"
        title="Switch model"
        aria-haspopup="true"
        aria-expanded={isModelMenuOpen}
      >
        <span className="font-medium">{llmModelLabel}</span>
        <HiChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>

      {/* Lightweight in-place dropdown */}
      {isModelMenuOpen && (
        <div
          className={`absolute shadow-xl rounded-xl overflow-hidden
            ${menuMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'} 
            transition-all duration-200 ease-out bottom-[42px] left-0 w-[280px] z-[10000] 
            bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700
          `}
          onMouseDown={(e) => e.preventDefault()}
          role="menu"
          aria-orientation="vertical"
        >
          {/* Admin models */}
          {isAdmin && (
            <>
              <button
                className={`w-full px-4 py-3.5 text-left transition-all duration-150
                  ${currentModel === LLM_MODEL_TYPES.GEMINI_3_1_PRO 
                    ? 'bg-blue-50 dark:bg-blue-900/20' 
                    : 'hover:bg-gray-100 dark:hover:bg-slate-800'
                  }
                `}
                onClick={(e) => {
                  e.preventDefault();
                  setIsModelMenuOpen(false);
                  if (canChangeLlmModel) {
                    onChangeLLMModel(LLM_MODEL_TYPES.GEMINI_3_1_PRO);
                  } else {
                    navigate({ pathname: "/settings", search: "", hash: SettingType.LLM });
                  }
                }}
                role="menuitem"
                tabIndex={0}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-sm font-medium ${currentModel === LLM_MODEL_TYPES.GEMINI_3_1_PRO ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-100'}`}>
                        {MODEL_CONFIG[LLM_MODEL_TYPES.GEMINI_3_1_PRO].name}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-green-500/90 text-white font-bold uppercase tracking-wide">NEW</span>
                    </div>
                    <span className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">{MODEL_CONFIG[LLM_MODEL_TYPES.GEMINI_3_1_PRO].description}</span>
                  </div>

                  <div className="flex items-center shrink-0">
                    {!canChangeLlmModel && (
                      <span className="text-[10px] px-2 py-1 rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-semibold whitespace-nowrap">Use Key</span>
                    )}

                    {canChangeLlmModel && currentModel === LLM_MODEL_TYPES.GEMINI_3_1_PRO && (
                      <span className="text-blue-600 dark:text-blue-400 text-[11px] font-semibold">Selected</span>
                    )}
                  </div>
                </div>
              </button>
              <div className="h-[1px] bg-gray-200/50 dark:bg-gray-700/50" />
              <button
                className={`w-full px-4 py-3.5 text-left transition-all duration-150
                  ${currentModel === LLM_MODEL_TYPES.GEMINI_3_0_PRO 
                    ? 'bg-blue-50 dark:bg-blue-900/20' 
                    : 'hover:bg-gray-100 dark:hover:bg-slate-800'
                  }
                `}
                onClick={(e) => {
                  e.preventDefault();
                  setIsModelMenuOpen(false);
                  if (canChangeLlmModel) {
                    onChangeLLMModel(LLM_MODEL_TYPES.GEMINI_3_0_PRO);
                  } else {
                    navigate({ pathname: "/settings", search: "", hash: SettingType.LLM });
                  }
                }}
                role="menuitem"
                tabIndex={0}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className={`text-sm font-medium mb-1 ${currentModel === LLM_MODEL_TYPES.GEMINI_3_0_PRO ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-100'}`}>
                      {MODEL_CONFIG[LLM_MODEL_TYPES.GEMINI_3_0_PRO].name}
                    </span>
                    <span className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">{MODEL_CONFIG[LLM_MODEL_TYPES.GEMINI_3_0_PRO].description}</span>
                  </div>

                  <div className="flex items-center shrink-0">
                    {!canChangeLlmModel && (
                      <span className="text-[10px] px-2 py-1 rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-semibold whitespace-nowrap">Use Key</span>
                    )}

                    {canChangeLlmModel && currentModel === LLM_MODEL_TYPES.GEMINI_3_0_PRO && (
                      <span className="text-blue-600 dark:text-blue-400 text-[11px] font-semibold">Selected</span>
                    )}
                  </div>
                </div>
              </button>
              <div className="h-[1px] bg-gray-200/50 dark:bg-gray-700/50" />
            </>
          )}

          {/* Gemini 3 Flash - Default, no key required */}
          <button
            className={`w-full px-4 py-3.5 text-left transition-all duration-150
              ${currentModel === LLM_MODEL_TYPES.GEMINI_3_0_FLASH 
                ? 'bg-blue-50 dark:bg-blue-900/20' 
                : 'hover:bg-gray-100 dark:hover:bg-slate-800'
              }
            `}
            onClick={(e) => {
              e.preventDefault();
              setIsModelMenuOpen(false);
              onChangeLLMModel(LLM_MODEL_TYPES.GEMINI_3_0_FLASH);
            }}
            role="menuitem"
            tabIndex={0}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col flex-1 min-w-0">
                <span className={`text-sm font-medium mb-1 ${currentModel === LLM_MODEL_TYPES.GEMINI_3_0_FLASH ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-100'}`}>
                  {MODEL_CONFIG[LLM_MODEL_TYPES.GEMINI_3_0_FLASH].name}
                </span>
                <span className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">{MODEL_CONFIG[LLM_MODEL_TYPES.GEMINI_3_0_FLASH].description}</span>
              </div>

              <div className="flex items-center shrink-0">
                {currentModel === LLM_MODEL_TYPES.GEMINI_3_0_FLASH && (
                  <span className="text-blue-600 dark:text-blue-400 text-[11px] font-semibold">Selected</span>
                )}
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  )
}

export default ChangeLLMModel;